import {inject, Injectable} from '@angular/core'
import {HttpClient, HttpContext} from '@angular/common/http'
import {IExchangeResponse, IGitLabAuth} from '../gitlab/authenticated/authenticated'
import {environment} from '../../environments/environment'
import {LOCAL_STORAGE} from '../application/app'
import {catchError, Observable, of, tap} from 'rxjs'
import {map} from 'rxjs/operators'
import {GITLAB_ACCESS_TOKEN} from '../application/auth-interceptor'

const ACCESS_TOKEN_NAME = 'gitLabAccessToken'
export const GITLAB_HOST = 'https://gitlab.com/api/v4'

@Injectable({
  providedIn: 'root'
})
export class GitlabService {

  private readonly http = inject(HttpClient)

  private storage = inject(LOCAL_STORAGE)

  private context = new HttpContext()

  /**
   * Returns the access token
   */
  public gitLabAccessToken(): Observable<string | null> {
    return this.checkToken().pipe(
      map(token => {
        return token ? token.access_token : null
      }))
  }

  /**
   * Converts a code to a real token after authentication.
   * @param state
   */
  public exchange(state: string) {
    return this.http.put<IExchangeResponse>(
      `${environment.gitLabApiHost}/exchange`, {state, nonce: 'nonce'})
      .pipe(tap(res => {
        this.storage.setItem(ACCESS_TOKEN_NAME, JSON.stringify(res.gitlab))
      }))
  }

  /**
   * Check if a token is valid and actually tries to use it.
   */
  private checkToken(): Observable<IGitLabAuth | null> {
    const stored = this.storage.getItem(ACCESS_TOKEN_NAME)
    if (stored) {
      const token = JSON.parse(stored) as IGitLabAuth
      if (token.expires < Date.now()) {
        return this.refreshToken(token.refresh_token)
      } else {
        this.context.set(GITLAB_ACCESS_TOKEN, token.access_token)
        return this.http.get(`${GITLAB_HOST}/user`, {context: this.context})
          .pipe(
            map(() => token),
            catchError(() => {
              return this.refreshToken(token.refresh_token)
            })
          )
      }
    }
    return of(null)
  }

  private refreshToken(token: string): Observable<IGitLabAuth | null> {
    return this.http.get<IGitLabAuth>(`${environment.gitLabApiHost}/refresh-gitlab?rt=${token}`)
      .pipe(
        // If successful refresh set the user.
        tap(res => this.storage.setItem(ACCESS_TOKEN_NAME, JSON.stringify(res))),
        catchError(() => of(null)))
  }
}
